import { GRAY_COLOR, WATSAPP_ACTIVE } from "@constants/colors"
import Box from "@mui/material/Box"
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowModel
} from "@mui/x-data-grid"
import { formatDate } from "@utils/functions/formatDate"

import DataTableToolbar from "@components/data_table_toolbar"
import DeleteCoupon from "@components/dialogs/delete_coupon"
import GridTable from "@components/grid-table"
import { useCouponsPageContext } from "@contexts/main/coupons_page_context"
import { Stack } from "@mui/material"
import { useState } from "react"
import styles from "./styles"

export type DataTableRow = {
  _id: string
  name: string
  code: string
  status: string
  valueType: string
  value: number
  maxRedemptions: number
  timesRedeemed: number
  usageType: string
  expireAt: string
  createdDate: string
}

export default function CouponsDataTable() {
  const {
    translate,
    couponsData,
    setSearch,
    isLoading,
    pagination,
    setPagination,
    toggleDeleteCoupons
  } = useCouponsPageContext()
  const [selectedRow, setSelectedRow] = useState<GridRowModel[]>([])

  const fieldWidth = 100

  const columns: GridColDef[] = [
    {
      field: "name",
      minWidth: fieldWidth,
      description: "fixed",
      headerName: translate("name"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("name")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={styles.renderCellIconBox}>
            <Box>{params.value}</Box>
          </Box>
        )
      },

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "code",
      minWidth: 150,

      headerName: translate("code"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("code")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return <Box sx={styles.headerItems}>{params.value}</Box>
      },

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "status",
      minWidth: fieldWidth,
      headerName: translate("status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: params.value === "ACTIVE" ? WATSAPP_ACTIVE : GRAY_COLOR
          }}
        >
          {params.value.charAt(0) + params.value.substring(1).toLowerCase()}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "valueType",
      minWidth: fieldWidth,

      headerName: translate("value_type"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("value_type")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={styles.headerItems}>
            {params.value.charAt(0) + params.value.substring(1).toLowerCase()}
          </Box>
        )
      },

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "value",
      minWidth: fieldWidth,

      headerName: translate("value"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("value")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => <Box>{params.value}</Box>,

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "maxRedemptions",
      minWidth: fieldWidth,

      headerName: translate("max_redemptions"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("max_redemptions")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => <Box>{params.value}</Box>,

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "timesRedeemed",
      minWidth: fieldWidth,

      headerName: translate("times_redeemed"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("times_redeemed")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => <Box>{params.value}</Box>,

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "usageType",
      minWidth: 150,

      headerName: translate("usage_type"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("usage_type")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.value.charAt(0) + params.value.substring(1).toLowerCase()}
        </Box>
      ),

      sortable: true,
      disableColumnMenu: true
    },
    {
      field: "expiredAt",
      minWidth: 180,
      headerName: translate("expire_at"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("expire_at")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {formatDate(translate("date_format"), params.value as string)}
        </Box>
      ),

      disableColumnMenu: true
    },
    {
      field: "createdAt",
      minWidth: 180,
      headerName: translate("created_date"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("created_date")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.value
            ? formatDate(translate("date_format"), params.value as string)
            : "-"}
        </Box>
      ),

      disableColumnMenu: true
    },
    {
      field: "rightIcon",
      minWidth: 100,
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      flex: 1,
      align: "right",
      headerAlign: "right"
    }
  ]

  return (
    <Stack sx={{ overflow: "hidden", height: "100%", p: 2 }}>
      <DataTableToolbar
        onSearch={setSearch}
        hideBulkAction={false}
        concatProps={{ toggleDeleteCoupons }}
      />
      <GridTable
        sortable
        loading={isLoading}
        rows={couponsData as unknown as DataTableRow[]}
        columns={columns}
        rowCount={(couponsData as unknown as DataTableRow[])?.length}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row: DataTableRow) => row._id}
        disableColumnSelector={true}
        checkboxSelection
        onRowSelectionModelChange={(params) => {
          const selectedIDs = new Set(params)
          const selectedRowData = couponsData?.filter((row) =>
            selectedIDs.has(row?._id as string)
          )
          setSelectedRow(selectedRowData as GridRowModel[])
        }}
      />
      <DeleteCoupon
        selectedRowIds={selectedRow.map((item) => item._id as string)}
      />
    </Stack>
  )
}
